/**
 * The managed Playwright MCP server's loopback restriction, rendered as an
 * additive `runtime_instructions` contribution to the host's system prompt.
 *
 * The managed server is configured to reach `localhost` only. The agent has no
 * way to see that from the tool definitions, so it spends turns navigating to
 * external sites and reading back errors.
 *
 * `runtime_instructions` is the host's section for "client-specific tool
 * guidance", which is what a constraint on one tool's reach is —
 * `environment_context` carries facts about where the session runs.
 *
 * Only the constraint is stated. What to do about it is left to the model.
 */
import type {SystemMessageSections} from './system-message-sections'

/** Section id the host accepts for situational, tool-specific guidance. */
const RUNTIME_INSTRUCTIONS_SECTION = 'runtime_instructions'

const PLAYWRIGHT_LOCALHOST_CONTENT = [
  '## Playwright MCP',
  'Playwright MCP can only reach `localhost` and `127.0.0.1`.',
].join('\n')

/**
 * Build the Playwright contribution for a session whose daemon runs the managed
 * Playwright MCP server, or `undefined` when it does not — a user-managed host
 * runs the user's own MCP configuration, where the restriction does not hold.
 */
export function buildPlaywrightLocalhostSections(
  managedPlaywrightMcp: boolean | undefined,
): SystemMessageSections | undefined {
  if (!managedPlaywrightMcp) return undefined
  return {
    [RUNTIME_INSTRUCTIONS_SECTION]: {action: 'append', content: PLAYWRIGHT_LOCALHOST_CONTENT},
  }
}
